import { ReactElement } from 'react';

import {
  AppIcon,
  BackrsFormValues,
  CareerCategory,
  ContentNugget,
  NuggetVariant,
  ICON_SIZES,
  NUGGET_TYPES,
  NUGGET_VARIANTS,
  getCompetencySticker,
  careerPathTransform,
  collegeTransform,
  employerTransform,
  externalProfileTransform,
  internshipTransform,
  profileTransform,
  scholarshipTransform,
  competencyTransform,
  trackAssessmentResultTransform,
  trackAssessmentUnlockTransform,
  educationalInvestmentTransform,
  toolTransform,
  careerTrackTransform,
  majorTransform,
  industryTransform,
  coachTransform,
  deepDiveTransform,
  blueprintTransform,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { getCareerPathIcon } from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import {
  PriorityTask,
  PriorityTaskPost,
  TaskContentCode,
  TaskTypeCode,
  TaskStatusCode,
  PRIORITY_TASK_STATUS_CODES,
  PRIORITY_TASK_TYPE_CODES,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Theme, SxProps } from '@mui/material';

export const getToggledTaskStatusCode = (statusCode: TaskStatusCode | null): TaskStatusCode =>
  statusCode === PRIORITY_TASK_STATUS_CODES.COMPLETED
    ? PRIORITY_TASK_STATUS_CODES.NOT_STARTED
    : PRIORITY_TASK_STATUS_CODES.COMPLETED;

/** essentially just parses the RHF string form values into the correct types for the API */
export const constructTaskForPostPut = (data: BackrsFormValues): Partial<PriorityTaskPost> => {
  const getPriority = (priority: string) => {
    switch (priority) {
      case '1':
        return 1;
      case '2':
        return 2;
      case '3':
        return 3;
      default:
        return null;
    }
  };

  const getDueDate = (dueDate: string) => {
    if (!dueDate) return null;

    return dueDate;
  };

  const getContentId = (contentId: string) => {
    if (!contentId) return null;

    // Blueprint uses a UUID string content ID (career_user_response_set.id)
    if (contentId.includes('-')) return contentId;

    return parseInt(contentId);
  };

  const getPlaylistId = (playlistId: string) => {
    if (!playlistId) return null;

    return parseInt(playlistId);
  };

  const getTypeCode = (typeCode: string) => {
    if (!typeCode) return null;

    // typeguard
    if (Object.values(PRIORITY_TASK_TYPE_CODES).includes(typeCode as TaskTypeCode)) {
      return typeCode as TaskTypeCode;
    }

    return null;
  };

  const getStatusCode = (statusCode: string) => {
    if (!statusCode) return null;

    // typeguard
    if (Object.values(PRIORITY_TASK_STATUS_CODES).includes(statusCode as TaskStatusCode)) {
      return statusCode as TaskStatusCode;
    }

    return null;
  };

  const getTags = (tags: string | string[]) => {
    if (!tags) return [];

    const tagsArray = Array.isArray(tags) ? tags : tags.split(',').map((tag) => tag.trim());

    const uniqueTags = tagsArray.filter((tag, index, self) => self.indexOf(tag) === index);

    return uniqueTags;
  };

  return {
    title: data.title || '',
    description: data.description || null,
    priority: getPriority(data.priority),
    due_date: getDueDate(data.due_date),
    content_url: data.content_url || null,
    content_id: getContentId(data.content_id),
    type_code: getTypeCode(data.type_code),
    status_code: getStatusCode(data.status_code),
    playlist_id: getPlaylistId(data.playlist_id),
    tags: getTags(data.tags),
    content_code: data.content_code as TaskContentCode,
  };
};

export const determineTaskContentId = (
  nugget: ContentNugget | null,
): number | string | undefined => {
  if (!nugget) {
    return undefined;
  }

  // todo: update college search to return academic_profile_id as the id, instead of a prop
  if (
    nugget.variant === NUGGET_VARIANTS.INSTITUTION &&
    nugget.college_info &&
    !!nugget.college_info.academic_profile_id
  ) {
    return nugget.college_info.academic_profile_id;
  }

  // For educational investments, use the educational investment ID
  if (
    nugget.variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT &&
    nugget.educational_investment_info
  ) {
    return nugget.educational_investment_info.id;
  }

  if (nugget.variant === NUGGET_VARIANTS.BLUEPRINT) {
    return nugget.blueprint_info?.career_user_response_set_id;
  }

  return nugget.id;
};

export const determineTaskContentCode = (
  nugget: ContentNugget | null,
): TaskContentCode | undefined => {
  if (!nugget) {
    return undefined;
  }

  if (nugget.variant === NUGGET_VARIANTS.SCHOLARSHIPS) {
    return 'scholarships';
  }

  if (nugget.variant === NUGGET_VARIANTS.INTERNSHIP) {
    return 'internships';
  }

  if (nugget.variant === NUGGET_VARIANTS.CAREER_PATH) {
    return 'careerPaths';
  }

  if (nugget.variant === NUGGET_VARIANTS.CAREER_TRACK) {
    return 'careerTracks';
  }

  if (nugget.variant === NUGGET_VARIANTS.PROFILE_INTERNAL) {
    return 'profiles';
  }

  if (nugget.variant === NUGGET_VARIANTS.PROFILE_EXTERNAL) {
    return 'externalProfiles';
  }

  if (
    nugget.variant === NUGGET_VARIANTS.INSTITUTION &&
    nugget.college_info &&
    !!nugget.college_info.id
  ) {
    return 'academicProfiles';
  }

  if (nugget.variant === NUGGET_VARIANTS.EMPLOYER) {
    return 'employers';
  }

  if (nugget.variant === NUGGET_VARIANTS.COMPETENCY) {
    return 'competencies';
  }

  if (nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT) {
    return 'trackAssessmentResults';
  }

  if (nugget.variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK) {
    return 'trackAssessmentUnlocks';
  }

  if (nugget.variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT) {
    return 'educationalInvestments';
  }

  if (nugget.variant === NUGGET_VARIANTS.MAJOR) {
    return 'majors';
  }

  if (nugget.variant === NUGGET_VARIANTS.INDUSTRY) {
    return 'industries';
  }

  if (nugget.variant === NUGGET_VARIANTS.COACH) {
    return 'coaches';
  }

  if (nugget.variant === NUGGET_VARIANTS.DEEP_DIVE) {
    return 'deepDives';
  }

  if (nugget.variant === NUGGET_VARIANTS.BLUEPRINT) {
    return 'blueprints';
  }

  if (nugget.type === NUGGET_TYPES.TOOL) {
    return 'tools';
  }

  return 'contentNuggets';
};

export const determineTaskAutocompleteContentCode = (
  variant: NuggetVariant | undefined,
  hasRelatedCollegeInfo = false,
) => {
  if (!variant) {
    return undefined;
  }

  if (variant === NUGGET_VARIANTS.SCHOLARSHIPS) {
    return 'scholarships';
  }

  if (variant === NUGGET_VARIANTS.INTERNSHIP) {
    return 'internships';
  }

  if (variant === NUGGET_VARIANTS.CAREER_PATH) {
    return 'careerPaths';
  }

  if (variant === NUGGET_VARIANTS.CAREER_TRACK) {
    return 'careerTracks';
  }

  if (variant === NUGGET_VARIANTS.PROFILE_INTERNAL) {
    return 'profiles';
  }

  if (variant === NUGGET_VARIANTS.PROFILE_EXTERNAL) {
    return 'externalProfiles';
  }

  if (variant === NUGGET_VARIANTS.INSTITUTION && hasRelatedCollegeInfo) {
    return 'academicProfiles';
  }

  if (variant === NUGGET_VARIANTS.EMPLOYER) {
    return 'employers';
  }

  if (variant === NUGGET_VARIANTS.COMPETENCY) {
    return 'competencies';
  }

  if (variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT) {
    return 'trackAssessmentResults';
  }

  if (variant === NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK) {
    return 'trackAssessmentUnlocks';
  }

  if (variant === NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT) {
    return 'educationalInvestments';
  }

  if (variant === NUGGET_VARIANTS.MAJOR) {
    return 'majors';
  }

  if (variant === NUGGET_VARIANTS.INDUSTRY) {
    return 'industries';
  }

  if (variant === NUGGET_VARIANTS.COACH) {
    return 'coaches';
  }

  if (variant === NUGGET_VARIANTS.DEEP_DIVE) {
    return 'deepDives';
  }

  if (variant === NUGGET_VARIANTS.BLUEPRINT) {
    return 'blueprints';
  }

  if (variant === NUGGET_TYPES.TOOL) {
    return 'tools';
  }

  return 'contentNuggets';
};

export const determineContentCodeFromTask = (
  task: Partial<PriorityTask>,
): TaskContentCode | undefined => {
  if (task.scholarships && task.scholarships.length > 0) {
    return 'scholarships';
  }

  if (task.internships && task.internships.length > 0) {
    return 'internships';
  }

  if (task.careerPaths && task.careerPaths.length > 0) {
    return 'careerPaths';
  }

  if (task.careerTracks && task.careerTracks.length > 0) {
    return 'careerTracks';
  }

  if (task.profiles && task.profiles.length > 0) {
    return 'profiles';
  }

  if (task.externalProfiles && task.externalProfiles.length > 0) {
    return 'externalProfiles';
  }

  if (task.academicProfiles && task.academicProfiles.length > 0) {
    return 'academicProfiles';
  }

  if (task.employers && task.employers.length > 0) {
    return 'employers';
  }

  if (task.competencies && task.competencies.length > 0) {
    return 'competencies';
  }

  if (task.trackAssessmentResults && task.trackAssessmentResults.length > 0) {
    return 'trackAssessmentResults';
  }

  if (task.trackAssessmentUnlocks && task.trackAssessmentUnlocks.length > 0) {
    return 'trackAssessmentUnlocks';
  }

  if (task.educationalInvestments && task.educationalInvestments.length > 0) {
    return 'educationalInvestments';
  }

  if (task.majors && task.majors.length > 0) {
    return 'majors';
  }

  if (task.industries && task.industries.length > 0) {
    return 'industries';
  }

  if (task.coaches && task.coaches.length > 0) {
    return 'coaches';
  }

  if (task.deepDives && task.deepDives.length > 0) {
    return 'deepDives';
  }

  if (task.blueprints && task.blueprints.length > 0) {
    return 'blueprints';
  }

  if (task.contentNuggets && task.contentNuggets.length > 0) {
    return 'contentNuggets';
  }

  return undefined;
};

/** this is needed for duplicating a task - we need to find the relevant content_id for the correct content type
 *
 * todo: if we ever support multiple pieces of content to a single task, this will need to be refactored
 */
export const determineContentIdFromTask = (task: Partial<PriorityTask>): number | undefined => {
  if (task.scholarships && task.scholarships.length > 0) {
    return task.scholarships[0].id;
  }

  if (task.internships && task.internships.length > 0) {
    return task.internships[0].id;
  }

  if (task.careerPaths && task.careerPaths.length > 0) {
    return task.careerPaths[0].id;
  }

  if (task.profiles && task.profiles.length > 0) {
    return task.profiles[0].id;
  }

  if (task.externalProfiles && task.externalProfiles.length > 0) {
    return task.externalProfiles[0].id;
  }

  if (task.academicProfiles && task.academicProfiles.length > 0) {
    return task.academicProfiles[0].id;
  }

  if (task.employers && task.employers.length > 0) {
    return task.employers[0].id;
  }

  if (task.competencies && task.competencies.length > 0) {
    return task.competencies[0].id;
  }

  if (task.trackAssessmentResults && task.trackAssessmentResults.length > 0) {
    return task.trackAssessmentResults[0].id;
  }

  if (task.trackAssessmentUnlocks && task.trackAssessmentUnlocks.length > 0) {
    return task.trackAssessmentUnlocks[0].id;
  }

  if (task.educationalInvestments && task.educationalInvestments.length > 0) {
    return task.educationalInvestments[0].id;
  }

  if (task.majors && task.majors.length > 0) {
    return task.majors[0].id;
  }

  if (task.industries && task.industries.length > 0) {
    return task.industries[0].id;
  }

  if (task.coaches && task.coaches.length > 0) {
    return task.coaches[0].id;
  }

  if (task.deepDives && task.deepDives.length > 0) {
    return task.deepDives[0].id;
  }

  if (task.contentNuggets && task.contentNuggets.length > 0) {
    return task.contentNuggets[0].id;
  }

  return undefined;
};

export const transformTaskContentsToNuggets = (task: PriorityTask): ContentNugget[] => {
  const scholarships =
    task.scholarships?.map((scholarship) => scholarshipTransform(scholarship)) || [];
  const profiles = task.profiles?.map((profile) => profileTransform(profile)) || [];
  const careerPaths = task.careerPaths?.map((careerPath) => careerPathTransform(careerPath)) || [];
  const internships = task.internships?.map((internship) => internshipTransform(internship)) || [];
  const externalProfiles =
    task.externalProfiles?.map((externalProfile) => externalProfileTransform(externalProfile)) ||
    [];
  const academicProfiles = task.academicProfiles?.map((college) => collegeTransform(college)) || [];
  const employers = task.employers?.map((employer) => employerTransform(employer)) || [];
  const competencies =
    task.competencies?.map((competency) => competencyTransform(competency)) || [];
  const trackAssessmentResults =
    task.trackAssessmentResults?.map((result) => trackAssessmentResultTransform(result)) || [];
  const trackAssessmentUnlocks =
    task.trackAssessmentUnlocks?.map((unlock) => trackAssessmentUnlockTransform(unlock)) || [];
  const educationalInvestments =
    task.educationalInvestments?.map((investment) => educationalInvestmentTransform(investment)) ||
    [];
  const tools = task.tools?.map((tool) => toolTransform(tool)) || [];
  const careerTracks =
    task.careerTracks?.map((careerTrack) => careerTrackTransform(careerTrack)) || [];
  const majors = task.majors?.map((major) => majorTransform(major)) || [];
  const industries = task.industries?.map((industry) => industryTransform(industry)) || [];
  const coaches = task.coaches?.map((coach) => coachTransform(coach)) || [];
  const deepDives = task.deepDives?.map((deepDive) => deepDiveTransform(deepDive)) || [];
  const blueprints = task.blueprints?.map((blueprint) => blueprintTransform(blueprint.id)) || [];

  return [
    ...(task.contentNuggets || []),
    ...scholarships,
    ...profiles,
    ...careerPaths,
    ...internships,
    ...externalProfiles,
    ...academicProfiles,
    ...employers,
    ...competencies,
    ...trackAssessmentResults,
    ...trackAssessmentUnlocks,
    ...educationalInvestments,
    ...tools,
    ...careerTracks,
    ...majors,
    ...industries,
    ...coaches,
    ...deepDives,
    ...blueprints,
  ].filter((nugget) => nugget !== undefined);
};

export const getTaskDialogDefaultValues = ({
  taskToEdit,
  nuggetToAdd,
  currentlySelectedPlaylist,
  defaultTypeId,
  preloadedTaskInfo,
}: {
  taskToEdit?: PriorityTask | null;
  nuggetToAdd: ContentNugget | null;
  currentlySelectedPlaylist: PriorityPlaylist | null;
  defaultTypeId: string;
  preloadedTaskInfo?: Partial<PriorityTask>;
}): BackrsFormValues => {
  // Early return for editing existing task
  if (taskToEdit) {
    const existingNugget = transformTaskContentsToNuggets(taskToEdit)[0];
    const contentId = taskToEdit.content_id || existingNugget?.id;

    return {
      title: taskToEdit.title || '',
      description: taskToEdit.description || '',
      playlist_id: taskToEdit.playlist_id?.toString() || '',
      type_code: taskToEdit.type_code || defaultTypeId,
      due_date: taskToEdit.due_date
        ? new Date(taskToEdit.due_date).toISOString().split('T')[0]
        : '',
      priority: taskToEdit.priority?.toString() || '',
      content_id: contentId?.toString() || '',
      content_url: taskToEdit.content_url || '',
      tags: taskToEdit.tags?.map((tag) => tag.title).join(', ') || '',
      status_code: taskToEdit.status_code || '',
    };
  }

  const getDefaultTitle = () => {
    if (nuggetToAdd?.title) {
      return nuggetToAdd.title.length > 47
        ? nuggetToAdd.title.slice(0, 47) + '...'
        : nuggetToAdd.title;
    }

    return preloadedTaskInfo?.title || '';
  };

  const getDefaultPlaylistId = () => {
    return (
      preloadedTaskInfo?.playlist_id?.toString() || currentlySelectedPlaylist?.id?.toString() || ''
    );
  };

  const getDefaultDueDate = () => {
    if (!preloadedTaskInfo?.due_date) return '';

    return new Date(preloadedTaskInfo.due_date).toISOString().split('T')[0];
  };

  const getDefaultTags = () => {
    if (!preloadedTaskInfo?.tags) return '';

    const tagTitles = preloadedTaskInfo.tags.map((tag) =>
      typeof tag === 'string' ? tag : tag.title,
    );

    return tagTitles.join(', ');
  };

  const nuggetToAddContentCode = determineTaskContentCode(nuggetToAdd);

  // Determine content code from preloaded task content if no nugget is being added
  const preloadedTaskContentCode =
    !nuggetToAddContentCode && preloadedTaskInfo
      ? determineContentCodeFromTask(preloadedTaskInfo)
      : undefined;

  const finalContentCode = nuggetToAddContentCode || preloadedTaskContentCode;

  return {
    title: getDefaultTitle(),
    description: preloadedTaskInfo?.description || '',
    playlist_id: getDefaultPlaylistId(),
    type_code: preloadedTaskInfo?.type_code || defaultTypeId,
    due_date: getDefaultDueDate(),
    priority: preloadedTaskInfo?.priority?.toString() || '',
    content_id: determineTaskContentId(nuggetToAdd)?.toString() || '',
    content_url: preloadedTaskInfo?.content_url || '',
    tags: getDefaultTags(),
    status_code: preloadedTaskInfo?.status_code || '',
    ...(finalContentCode && {
      content_code: finalContentCode,
    }),
  };
};

export const getTaskImageUrl = (
  nugget: ContentNugget,
  careerCategories: CareerCategory[],
): { type: 'URL'; url?: string } | { type: 'COMPONENT'; component: ReactElement } | undefined => {
  // Special handling for video variants
  if (nugget.variant === 'video-external' || nugget.variant === 'video-internal') {
    // Try preview_media first
    if (nugget.preview_media?.media_url) {
      return { type: 'URL', url: nugget.preview_media.media_url };
    }

    // For external videos (like YouTube), try to generate thumbnail
    if (nugget.variant === 'video-external' && nugget.external_url) {
      const YOUTUBE_REGEX =
        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;
      const youtubeVideoId = nugget.external_url.match(YOUTUBE_REGEX)?.[5];

      if (youtubeVideoId) {
        return { type: 'URL', url: `https://img.youtube.com/vi/${youtubeVideoId}/0.jpg` };
      }
    }

    // For internal videos, try main media
    if (nugget.variant === 'video-internal' && nugget.media?.media_url) {
      return { type: 'URL', url: nugget.media.media_url };
    }

    return undefined;
  }

  if (nugget.variant === 'career-path' && nugget.career_path_info) {
    const icon = getCareerPathIcon(careerCategories, nugget.career_path_info);

    const RenderedComponent = (
      <Box sx={stickerContainerCareerPathSx}>
        <AppIcon iconName={icon} diameter="32px" color="Colors/Base/Black" />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'competency' && nugget.slug) {
    const competencySticker = getCompetencySticker(nugget.slug);

    return {
      type: 'URL',
      url: competencySticker,
    };
  }

  // Try preview_media first (most common for non-video variants)
  if (nugget.preview_media?.media_url) {
    return { type: 'URL', url: nugget.preview_media.media_url };
  }

  // Try main media
  if (nugget.media?.media_url) {
    return { type: 'URL', url: nugget.media.media_url };
  }

  // Variant-specific image properties
  switch (nugget.variant) {
    case 'profile-external':
      return { type: 'URL', url: nugget.external_profile_info?.profile_image_url };
    case 'profile-internal':
      return { type: 'URL', url: nugget.profile_info?.profile_image };
    case 'employer':
      return { type: 'URL', url: nugget.employer_info?.logo_media_url };
    default:
      return undefined;
  }
};

const stickerContainerCareerPathSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});
